'use client'

import { Button, Title, Text, Group, Stack } from '@mantine/core'
import { useRouter } from 'next/navigation'
import { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react'
import { Avatar } from './Avatar'
import { UserWithMentor } from '@/types/mentor'
import MentorSearchInput from './MentorSearchInput'
import ScrollHint from './ScrollHint'

interface HomeHeaderProps {
  floatingMentors: UserWithMentor[]
}

export default function HomeHeader({ floatingMentors }: HomeHeaderProps) {
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')
  const [scrollY, setScrollY] = useState(0)
  const [animationTime, setAnimationTime] = useState(0)
  const [yellowSectionBottom, setYellowSectionBottom] = useState(0)
  const [showFloatingAvatars, setShowFloatingAvatars] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const animate = () => {
      setAnimationTime(Date.now())
      requestAnimationFrame(animate)
    }
    const animationId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationId)
  }, [])

  // Use layoutEffect to measure before paint, preventing layout shift
  useLayoutEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setYellowSectionBottom(rect.bottom + window.scrollY)
    }
  }, [])

  // Defer floating avatars until after initial render to not block LCP
  useEffect(() => {
    // Wait for next frame to ensure main content is painted first
    const timeoutId = setTimeout(() => {
      setShowFloatingAvatars(true)
    }, 100)
    return () => clearTimeout(timeoutId)
  }, [])

  // Only update on resize, not on every scroll
  useEffect(() => {
    const updateYellowSectionBottom = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setYellowSectionBottom(rect.bottom + window.scrollY)
      }
    }
    
    window.addEventListener('resize', updateYellowSectionBottom)
    return () => window.removeEventListener('resize', updateYellowSectionBottom)
  }, [])

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    
    if (searchQuery.trim()) {
      // Store the search query in sessionStorage so the mentors page can pick it up
      sessionStorage.setItem('homeSearchQuery', searchQuery.trim())
      router.push('/mentors')
    } else {
      router.push('/mentors')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', minHeight: '500px' }}>
      {/* Floating mentor photos - deferred to not block LCP */}
      {showFloatingAvatars && floatingMentors.map((mentor, index) => {
        const speed = 10 + (index * 2) // Double the speed (pixels per second)
        const animationOffset = index * 2000 // Stagger start times by 2 seconds
        
        // Start from yellow section bottom (use measured value or fallback) - no scroll acceleration
        const yellowBottom = yellowSectionBottom > 0 ? yellowSectionBottom : 600 // Fallback to ~600px if not measured
        const startY = yellowBottom
        
        // Calculate Y position based on animation time
        const totalDistance = startY + 200 // Distance to travel upward from yellow section
        const cycleDuration = (totalDistance / speed) * 1000 // Duration in milliseconds
        const adjustedTime = (animationTime + animationOffset) % cycleDuration
        const progress = adjustedTime / cycleDuration
        const currentY = startY - (progress * totalDistance) // Start from yellow bottom, move up
        
        // Fade in/out based on Y position
        let opacity = 0.3
        if (currentY > startY - 100) {
          // Fade in when emerging from yellow section
          opacity = Math.max(0, (startY - currentY) / 100) * 0.3
        } else if (currentY < 100) {
          // Fade out when approaching top nav
          opacity = Math.max(0, currentY / 100) * 0.3
        }
        
        const photoSize = getPhotoSize(index)
        
        // Calculate z-index and blur based on photo size
        // Smaller photos = higher z-index, less blur
        // Larger photos = lower z-index, more blur
        const sizeNormalized = (photoSize - 80) / (130 - 80) // Normalize size to 0-1 range
        const photoZIndex = Math.round(5 - (sizeNormalized * 5)) // 5 for smallest, 0 for largest (behind mentor cards but visible)
        const blurAmount = Math.round(sizeNormalized * 16) // 0px for smallest, 16px for largest
        
        return (
          <div
            key={mentor.id}
            className="floating-photo"
            style={{
              position: 'fixed',
              opacity,
              pointerEvents: 'none',
              left: `${getPhotoPosition(index).x}%`,
              top: `${currentY}px`,
              zIndex: photoZIndex,
              filter: `blur(${blurAmount}px)`,
              transition: 'opacity 0.3s ease-out',
              willChange: 'transform, opacity', // Optimize for animation
              contain: 'layout style paint', // Isolate layout calculations
            }}
          >
            <Avatar
              userId={mentor.id}
              avatarId={mentor.avatarId}
              size={photoSize}
              radius="xl"
              loading="lazy"
              priority={false}
            />
          </div>
        )
      })}
      
      {/* Main content */}
      <div style={{ position: 'relative', zIndex: 20 }}>
        <Stack align="center" gap="xl" style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          textAlign: 'center', 
          padding: 'clamp(1rem, 4vw, 2rem)' 
        }}>
          <Stack gap="md" align="center">
            <Title
              size="clamp(2rem, 8vw, 3.5rem)"
              fw={700}
              className="gradient-emerald"
              style={{ lineHeight: 1.1 }}
            >
              Welcome to MentorSub
            </Title>
            <Text
              size="clamp(1.25rem, 3vw, 1.5rem)"
              c="emerald.7"
              fw={500}
            >
              Connect with industry leaders who will accelerate your career.
            </Text>
          </Stack>
          
          <div style={{ width: '100%', maxWidth: '600px' }}>
            <MentorSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={handleSearch}
              onKeyDown={handleKeyPress}
              showButton={false}
            />
          </div>
          
          <Group gap="md" justify="center">
            <Button
              variant="filled"
              color="navy"
              size="lg"
              onClick={handleSearch}
            >
              Browse mentors
            </Button>
            <Button
              variant="subtle"
              color="navy"
              size="lg"
              onClick={() => router.push('/become-a-mentor')}
            >
              Become a mentor
            </Button>
          </Group>
        </Stack>
      </div>
      
      {/* Animated scroll hint */}
      <ScrollHint scrollY={scrollY} />
    </div>
  )
}

// Helper function to position photos
function getPhotoPosition(index: number) {
  const positions = [
    { x: 10, y: 15 },   // top left
    { x: 85, y: 10 },   // top right
    { x: 5, y: 45 },    // middle left
    { x: 90, y: 40 },   // middle right
    { x: 15, y: 75 },   // bottom left
    { x: 80, y: 80 },   // bottom right
    { x: 45, y: 20 },   // top center
    { x: 50, y: 85 },   // bottom center
    { x: 25, y: 30 },   // mid-left upper
    { x: 70, y: 25 },   // mid-right upper
    { x: 35, y: 60 },   // mid-left lower
    { x: 65, y: 65 },   // mid-right lower
  ]
  return positions[index] || { x: 50, y: 50 }
}

// Helper function for photo sizes
function getPhotoSize(index: number) {
  const baseSizes = [120, 100, 80, 110, 90, 130, 85, 95, 105, 115, 95, 125]
  const size = baseSizes[index] || 100
  return Math.round(size * 1.25) // Make 25% bigger
}
